/**
 * Bomba Hero Staircase Native Web Component
 * Standard-compliant approach for absolute persistence.
 */
class BombaStaircase extends HTMLElement {
  constructor() {
    super();
    this.inner = null;
    this.observer = null;
  }

  connectedCallback() {
    // Hide content until component is ready to prevent FOUC
    this.style.visibility = 'hidden';
    
    // Small delay to ensure DOM parsing is complete on first load/reboot
    setTimeout(() => {
      this.init();
      this.setupObserver();
      this.style.visibility = 'visible';
    }, 1);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  init() {
    this.inner = this.querySelector('.bomba-hero__subtitle-inner, .bomba-hero__title-inner');
    if (!this.inner) return;

    // Get source text from template or attribute or content
    const template = this.querySelector('template[data-raw-text]');
    let rawText = '';
    
    if (template) {
      // Use innerHTML to keep line breaks, but STRIP Shopify Editor comments
      rawText = template.innerHTML.replace(/<!--[\s\S]*?-->/g, '');
    } else {
      rawText = this.getAttribute('data-subtitle') || this.inner.innerText;
    }
    
    const decodedText = this.decodeHtml(rawText).trim();
    
    // Clear and build segments
    this.inner.innerHTML = '';
    
    const splitType = this.getAttribute('data-split-type') || 'auto';
    const hasManualBreaks = decodedText.includes('\n') || decodedText.includes('<br');
    let finalSegments = [];

    // Prioritize manual breaks if they exist and splitType is manual or auto-with-manual
    if (hasManualBreaks) {
      finalSegments = decodedText.split(/\n|<br\s*\/?>/g)
        .map(s => s.trim())
        .filter(s => s.length > 0);
    } else if (splitType === 'auto') {
      // Fallback to sentence split for automatic mode
      let rawSegments = decodedText.split(/\. /g).filter(s => s.trim().length > 0);
      for (let i = 0; i < rawSegments.length; i++) {
        let segment = rawSegments[i].trim();
        if (decodedText.includes(segment + '.')) segment += '.';
        if (segment.length < 25 && finalSegments.length > 0) {
          finalSegments[finalSegments.length - 1] += ' ' + segment;
        } else {
          finalSegments.push(segment);
        }
      }
    } else {
      // Manual mode but no breaks found? Just use as is.
      finalSegments = [decodedText];
    }
    
    finalSegments.forEach((segment, index) => {
      const line = document.createElement('div');
      line.className = `bomba-hero__staircase-line line-${index + 1}`;
      line.innerText = segment;
      
      // Strict left alignment with staggered right padding/margin
      if (window.innerWidth >= 1024) {
        // We use margin-right to create the staircase on the right side
        // Line indices: 0, 1, 2... 
        // We want the right edge to recede as we go down
        line.style.marginRight = (index * 4) + 'rem';
        line.style.marginLeft = '0'; // Ensure NO left indentation
      } else {
        line.style.marginRight = '0';
        line.style.marginLeft = '0';
      }
      this.inner.appendChild(line);
    });
  }

  setupObserver() {
    // Watch for attribute changes (Shopify Editor) or inner content wipes
    this.observer = new MutationObserver((mutations) => {
      for (let mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-subtitle') {
          this.init();
        }
        // Fallback for editor direct HTML manipulation
        if (mutation.type === 'childList' && !this.querySelector('.bomba-hero__staircase-line')) {
          this.init();
        }
      }
    });

    this.observer.observe(this, { 
      attributes: true, 
      childList: true, 
      subtree: false 
    });
  }

  decodeHtml(html) {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }
}

// Define the component once
if (!customElements.get('bomba-staircase')) {
  customElements.define('bomba-staircase', BombaStaircase);
}
